<template>
  <div id="login-wrapper">
    <div class="login-content">
      <h1 class="main-title">
        <img :src="aperturaLogo" alt="Apertura Logo">
        <br>
        Where Science Becomes Art
      </h1>

      <div class="login-container">
        <h2>Login</h2>
        <form @submit.prevent="login">
          <div class="input-container">
            <label for="username">Username</label>
            <input type="text" id="username" autocomplete="current-username" v-model="username" required
                   :disabled="isLoading">
          </div>
          <div class="input-container">
            <label for="password">Password</label>
            <input type="password" id="password" autocomplete="current-password" v-model="password" required
                   :disabled="isLoading">
          </div>

          <span v-if="loginError" class="error-message">{{ loginError }}</span>


          <button type="submit" :disabled="isLoading" :class="{ 'loading': isLoading }" class="login-button">
            <span v-if="!isLoading">Login</span>
            <LoadingSpinner v-else :inline="true" size="16px" color="#ffffff" text="Logging in..."
                            text-color="#ffffff"/>
          </button>

          <p class="mt-10 mb-10">or</p>

          <button class="signup-button" @click.prevent="navigateToSignup">Sign up</button>


          <div class="forgot-password">
            <a href="#" @click.prevent="openDialog('datenschutz.html')">Data Protection</a> |
            <a href="#" @click.prevent="openDialog('nutzungsbedingungen.html')">Terms of Use</a>
          </div>

          <div class="logo-container">
            <img :src="bfhLogo" alt="BFH" class="logo logo-bfh">
            <img :src="uiucLogo" alt="BeLearn" class="logo logo-uiuc">
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref} from 'vue'
import {useRouter} from 'vue-router'

import dataPrivacyContent from '@/assets/datenschutz.html'
import termsOfUseContent from '@/assets/nutzungsbedingungen.html'

import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
import apiService from '@/services/apiService'

import bfhLogo from '@/assets/bfh-logo.png'
import uiucLogo from '@/assets/uiuc-logo.jpg'
import aperturaLogo from '@/assets/apertura_logo.png'

const emit = defineEmits(['login'])
const router = useRouter()

// --- state ---
const username = ref('')
const password = ref('')
const loginError = ref(null)
const isLoading = ref(false)

const isDialogVisible = ref(false)

const dialogContent = ref('')

// --- methods ---
function clearLoginError() {
  loginError.value = null
}

function openDialog(htmlFile) {
  if (htmlFile === 'datenschutz.html') {
    dialogContent.value = dataPrivacyContent
  } else if (htmlFile === 'nutzungsbedingungen.html') {
    dialogContent.value = termsOfUseContent
  }

  isDialogVisible.value = true
}

function closeDialog() {
  isDialogVisible.value = false
}

function navigateToSignup() {
  router.push('/signup')
}

async function login() {
  loginError.value = null
  isLoading.value = true

  try {
    const result = await apiService.login(username.value, password.value)

    if (result.success) {
      emit('login')

      router.push('/').catch(err => {
        console.error('Navigation failed:', err)
        window.location.href = '/'
      })
    } else {
      loginError.value = result.error
    }

  } catch (error) {
    console.error(error)
    loginError.value = 'An error occurred while trying to log in'
  } finally {
    isLoading.value = false
  }
}

</script>

<style scoped>
#login-wrapper {
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-image: linear-gradient(
      rgba(0, 0, 0, 0.5),
      rgba(0, 0, 0, 0.5)),
  url('/public/login-image.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  padding: 0.5rem;
  box-sizing: border-box;
}

.login-content {
  width: 100%;
  max-width: 90%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0.75rem 0;
}

.main-title {
  color: black;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  border: 1px solid #e0e0e0;
  width: 100%;
  max-width: 90%;
  margin: 0 auto 0.75rem auto;
  padding: 0.75rem;
  font-size: clamp(1rem, 2.5vw, 1.25rem);
  line-height: 1.3;
  font-weight: 600;
  box-sizing: border-box;
  text-align: center;
}

.main-title br {
  display: none;
}

.main-title img {
  max-width: 90%;
}

.login-container {
  width: 100%;
  max-width: 90%;
  padding: 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #fff;
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.08);
  margin: 0 auto;
  text-align: center;
  box-sizing: border-box;
}

.login-container h2 {
  color: #404040;
  margin-bottom: 0.75rem;
  font-weight: 600;
  font-size: clamp(1rem, 2.5vw, 1.25rem);
}

.input-container {
  margin-bottom: 0.75rem;
  text-align: left;
}

.input-container label {
  display: block;
  margin-bottom: 0.25rem;
  font-weight: 500;
  color: #505050;
  font-size: 0.75rem;
}

.input-container input {
  width: 100%;
  padding: 0.6rem 0.75rem;
  box-sizing: border-box;
  border-radius: 6px;
  border: 1px solid #d0d0d0;
  background-color: #ffffff;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  color: #333;
}

.input-container input:hover {
  border-color: #13294B;
}

.input-container input:focus {
  border-color: #13294B;
  outline: none;
}

.login-button {
  width: 100%;
  padding: 0.5rem;
  background-color: #13294B;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  transition: background-color 0.2s ease;
  margin-top: 0.5rem;
}

.login-button:hover:not(:disabled) {
  background-color: #224781;
}


.signup-button {
  width: 100%;
  padding: 0.5rem;
  background-color: #df8e22;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  transition: background-color 0.2s ease;
  margin-top: 0.5rem;
}

.signup-button:hover {
  background-color: #b87217;
}


button:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
}

.input-container input:disabled {
  background-color: #f5f5f5;
  color: #999;
  cursor: not-allowed;
}

.error-message {
  color: #e05252;
  margin-top: 0.5rem;
  display: block;
  font-size: 0.75rem;
}

.forgot-password {
  text-align: center;
}

.forgot-password a {
  color: #13294B;
  text-decoration: none;
  font-size: 0.75rem;
  transition: color 0.2s ease;
  word-break: break-word;
}

.forgot-password a:hover {
  color: #3e8d70;
  text-decoration: underline;
}

.logo-container {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 0.75rem;
  gap: 0.5rem;
}

.logo {
  max-width: 100%;
  height: auto;
  opacity: 0.9;
  transition: opacity 0.2s ease;
}

.logo-bfh {
  width: clamp(25px, 10%, 30px);
}

.logo-uiuc {
  width: clamp(60px, 25%, 80px);
}

.logo:hover {
  opacity: 1;
}

/* Media Queries */
@media screen and (min-height: 700px) {
  .login-content {
    padding: 1rem 0;
  }

  .main-title {
    margin-bottom: 1rem;
    padding: 1rem;
  }

  .login-container {
    padding: 1.25rem;
  }
}

@media screen and (min-width: 481px) and (min-height: 600px) {
  .login-content {
    max-width: 400px;
  }

  .main-title {
    max-width: 400px;
    font-size: 1.2rem;
  }

  .login-container {
    max-width: 400px;
  }

  .input-container label {
    font-size: 0.875rem;
  }

  .input-container input {
    padding: 0.625rem;
  }

  button {
    padding: 0.625rem;
  }

  .forgot-password a {
    font-size: 0.875rem;
  }
}

@media screen and (max-width: 480px) {
  #login-wrapper {
    align-items: flex-start;
    padding-top: 1rem;
  }
}

@media screen and (min-width: 768px) and (min-height: 768px) {
  .login-content {
    max-width: 450px;
  }

  .main-title {
    max-width: 450px;
    font-size: 1.25rem;
    padding: 1rem;
  }

  .login-container {
    max-width: 450px;
    padding: 1.5rem;
  }

  .input-container {
    margin-bottom: 1rem;
  }

  .input-container label {
    font-size: 0.875rem;
  }

  .input-container input {
    padding: 0.75rem;
    font-size: 1rem;
  }

  button {
    padding: 0.75rem;
    font-size: 1rem;
  }

  .links-container {
    gap: 0.75rem;
    margin-top: 1rem;
  }

  .forgot-password a {
    font-size: 0.875rem;
  }

  .logo-container {
    margin-top: 1rem;
  }
}

@media screen and (min-width: 1024px) and (min-height: 768px) {
  .main-title br {
    display: inline;
  }
}
</style>